import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/context/LanguageContext";
import api, { apiError } from "@/lib/api";

export default function ForgotPassword() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devToken, setDevToken] = useState(null);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      setDevToken(data.dev_token || null);
    } catch (e2) { setErr(apiError(e2)); } finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-md">
      <div className="mb-6 flex flex-col items-center text-center">
        <Logo size={64} />
        <h1 className="mt-4 text-2xl font-bold text-gray-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("auth.forgotTitle")}</h1>
        <p className="mt-1 text-sm text-gray-500">{t("auth.forgotSub")}</p>
      </div>

      {sent ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center" data-testid="forgot-sent">
          <MailCheck className="mx-auto mb-3 text-emerald-500" size={36} />
          <p className="text-sm font-medium text-emerald-800">
            If an account exists, a reset link has been sent.
          </p>
          {devToken && (
            <Link to={`/reset-password?token=${devToken}`} data-testid="forgot-dev-link"
              className="mt-4 inline-block rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white">
              {t("auth.resetTitle")}
            </Link>
          )}
        </div>
      ) : (
        <form onSubmit={submit} className="rounded-2xl border border-gray-200 bg-white p-6" data-testid="forgot-form">
          {err && <div className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-600">{err}</div>}
          <label className="mb-1.5 block text-sm font-semibold text-gray-700">{t("auth.email")}</label>
          <input data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="mb-5 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100" placeholder="you@email.com" />
          <button data-testid="forgot-submit-button" disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 py-3.5 text-sm font-bold text-white transition-colors hover:bg-emerald-600 disabled:opacity-60">
            {loading && <Loader2 size={16} className="animate-spin" />} {t("auth.sendReset")}
          </button>
        </form>
      )}
      <p className="mt-5 text-center text-sm">
        <Link to="/login" className="font-semibold text-emerald-600 hover:underline">{t("auth.backToLogin")}</Link>
      </p>
    </div>
  );
}
